import _ from 'lodash';
import { DEVMODE, deepFreeze } from 'shared/util';

import GameData from 'shared/gameData';

const BASE_COLUMN_WIDTH = 600;

// Prints out detailed node/record information for parse and stringify when enabled
const DEBUG_TRAIT_PARSING = DEVMODE && false;
const TABLE_REPORT_START = 0;
const TABLE_REPORT_COUNT = 20;
const DEBUG_GET_BITS = DEVMODE && false;

const mergeNodeNames = [
  'spec',
  'class',
  'subtree',
  'hero',
];
const mergeNodes = tree => ([
  tree.specNodes,
  tree.classNodes,
  tree.subTreeNodes,
  tree.heroNodes,
]);

// spec -> entry lookup
let traitEntryLookup;
// entry lookup used to verify if an entry belongs to a class
let allTraitEntryLookup;

const getTraitEntry = (specId, entryId) => {
  if (!traitEntryLookup) {
    traitEntryLookup = {};
    allTraitEntryLookup = {};
    _.each(GameData.NewTalents, tree => {
      _.each(mergeNodes(tree), (nodes, idx) => {
        const whichTree = mergeNodeNames[idx];
        _.each(nodes, node => {
          _.each(node.entries, entry => {
            if (!traitEntryLookup[tree.specId]) {
              traitEntryLookup[tree.specId] = {};
            }
            const entryObj = {
              ...entry,
              ...entry,
              nodeId: node.id,
              node,
              tree: whichTree,
              treeId: tree.traitTreeId,
              className: tree.className,
              classId: tree.classId,
            };
            traitEntryLookup[tree.specId][entry.id] = {
              ...entryObj,
              specName: tree.specName,
              specId: tree.specId,
            };
            if (!allTraitEntryLookup[entry.id]) {
              allTraitEntryLookup[entry.id] = entryObj;
            }
          });
        });
      });
    });
    deepFreeze(traitEntryLookup);
  }
  if (traitEntryLookup[specId]) {
    return traitEntryLookup[specId][entryId];
  }
  return null;
};

let allTraitNodeLookup;
let traitNodeLookup;
let subTreeLookup;
const buildLookups = () => {
  if (!traitNodeLookup) {
    allTraitNodeLookup = {};
    traitNodeLookup = {};
    subTreeLookup = {};
    _.each(GameData.NewTalents, tree => {
      traitNodeLookup[tree.specId] = {};
      _.each(mergeNodes(tree), (nodes, idx) => {
        const whichTree = mergeNodeNames[idx];
        _.each(nodes, node => {
          const obj = {
            ...node,
            tree: whichTree,
            treeId: tree.traitTreeId,
            className: tree.className,
            classId: tree.classId,
            specName: tree.specName,
            specId: tree.specId,
          };
          traitNodeLookup[tree.specId][node.id] = {
            ...obj,
            availableToSpec: true,
          };
          allTraitNodeLookup[node.id] = {
            ...obj,
          };
          if (obj.tree === 'subtree') {
            _.each(node.entries, ste => {
              subTreeLookup[ste.traitSubTreeId] = ste;
            });
          }
        });
      });
    });
    deepFreeze(traitNodeLookup);
  }
};
const getTraitNode = (nodeId, specId = null) => {
  buildLookups();
  if (traitNodeLookup[specId] && traitNodeLookup[specId][nodeId]) {
    return traitNodeLookup[specId][nodeId];
  }
  return allTraitNodeLookup[nodeId];
};

const getTreeBySpecId = _.memoize((specId) => {
  return _.find(GameData.NewTalents, { specId });
});
const getTreeByTreeId = _.memoize((traitTreeId) => {
  return _.find(GameData.NewTalents, { traitTreeId });
});

const getSubTreeById = (subTreeId) => {
  buildLookups();
  return subTreeLookup[subTreeId];
};

const isNodeInBounds = node => {
  return (
    node.posX > 0
    && node.posY > 0
  );
};

// gets all nodes for a spec and adjusts the X position to shrink the empty space between class/spec trees
const getDisplayAdjustedNodes = (specId, showHero = true) => {
  const tree = getTreeBySpecId(specId);

  const classNodes = _.cloneDeep(tree.classNodes);
  let specNodes = _.cloneDeep(tree.specNodes);
  let subTreeNodes = _.cloneDeep(tree.subTreeNodes);
  let heroNodes = _.cloneDeep(tree.heroNodes);

  const classRightSide = _.max(_.map(classNodes, 'posX'));

  // we go ahead and move all the hero nodes even if they will be hidden. This function doesn't care about that
  heroNodes = _.map(heroNodes, node => {
    const heroTreeBaseNode = _.find(heroNodes, n => n.subTreeId === node.subTreeId && n.prev.length === 0);
    const xOffset = node.posX - heroTreeBaseNode.posX;
    const yOffset = node.posY - heroTreeBaseNode.posY;
    return {
      ...node,
      posX: classRightSide + xOffset + (3 * BASE_COLUMN_WIDTH),
      posY: yOffset + (3.5 * BASE_COLUMN_WIDTH),
    };
  });

  const heroNodesRightSide = _.max(_.map(heroNodes, 'posX'));
  const heroNodesLeftSide = _.min(_.map(heroNodes, 'posX'));
  const heroNodesMiddle = heroNodesLeftSide + ((heroNodesRightSide - heroNodesLeftSide) / 2);
  const heroNodesTop = _.min(_.map(heroNodes, 'posY'));

  // move subtree nodes to the right of spec nodes
  subTreeNodes = _.map(subTreeNodes, node => ({
    ...node,
    posX: heroNodesMiddle - 165,
    posY: heroNodesTop - 800, // top align with spec
  }));

  // collapse space between class and spec nodes
  const specLeftSide = _.min(_.map(specNodes, 'posX'));
  let specOffset;
  if (showHero) {
    specOffset = heroNodesRightSide - specLeftSide;
  } else {
    specOffset = classRightSide - specLeftSide;
  }
  specNodes = _.map(specNodes, node => ({
    ...node,
    posX: node.posX + specOffset + (2 * BASE_COLUMN_WIDTH),
  }));

  return {
    classNodes: _.filter(classNodes, isNodeInBounds),
    specNodes: _.filter(specNodes, isNodeInBounds),
    subTreeNodes: showHero ? _.filter(subTreeNodes, isNodeInBounds) : [],
    heroNodes: showHero ? _.filter(heroNodes, isNodeInBounds) : [],
  };
};

const getBounds = nodes => {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  _.each(nodes, node => {
    if (node.posX < xMin) xMin = node.posX;
    if (node.posX > xMax) xMax = node.posX;
    if (node.posY < yMin) yMin = node.posY;
    if (node.posY > yMax) yMax = node.posY;
  });
  const xRange = xMax - xMin;

  const yRange = yMax - yMin;

  const numColumns = xRange / BASE_COLUMN_WIDTH;

  return {
    xMin,
    xMax,
    xRange,

    yMin,
    yMax,
    yRange,

    numColumns,
  };
};

const splitTalents = str => {
  const bits = _.filter(_.split(str, '/'));
  const entries = _.map(bits, s => {
    const [entryId, rank] = _.map(_.split(s, ':'), i => parseInt(i, 10));
    return {
      entryId,
      rank,
    };
  });
  return entries;
};

const base64Char = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const base64CharValue = _.fromPairs(_.map(base64Char, (char, index) => [char, index]));

const SUPPORTED_TALENT_SERIALIZATION_VERSION = 2;

const BYTE_SIZE = 6;
const VERSION_BITS = 8;
const SPEC_BITS = 16;
const TREE_BITS = 128;
const RANK_BITS = 6;
const CHOICE_BITS = 2;

const looksLikeBlizzard = str => {
  if (!str) {
    return false;
  }
  return _.every(str, ch => _.has(base64CharValue, ch));
};

const parseBlizzardTalents = str => {
  if (!str) {
    return '';
  }

  let head = 0;
  const getBits = (bits, msg) => {
    let value = 0;
    for (let i = 0; i < bits; i += 1) {
      const pos = Math.floor(head / BYTE_SIZE);
      if (DEBUG_GET_BITS && head % BYTE_SIZE === 0) {
        // eslint-disable-next-line no-console
        console.log(`Byte ${pos}:`, str[pos]);
      }
      const char = str[pos];
      const byte = base64CharValue[char];
      const bit = head % BYTE_SIZE;
      head += 1;
      // eslint-disable-next-line no-bitwise
      value += ((byte >> bit) & 0b1) << i;
    }
    if (DEBUG_GET_BITS && msg) {
      // eslint-disable-next-line no-console
      console.log(`${value}: ${msg}`);
    }
    return value;
  };


  const version = getBits(VERSION_BITS, 'version');
  if (version !== SUPPORTED_TALENT_SERIALIZATION_VERSION && DEBUG_TRAIT_PARSING) {
    // eslint-disable-next-line no-console
    console.error('Unhandled talent serialization version!');
  }
  const specId = getBits(SPEC_BITS, 'spec');

  const treeHash = getBits(TREE_BITS, 'treeHash');

  const treeData = getTreeBySpecId(specId);

  if (!treeData) {
    return '';
  }

  const entries = [];
  const unknownNodes = [];

  const records = [];

  for (let i = 0; i < treeData.fullNodeOrder.length; i += 1) {
    const nodeId = treeData.fullNodeOrder[i];
    const node = getTraitNode(nodeId, specId);

    const selected = getBits(1, `is_selected for ${node?.name} (${nodeId})`);

    let rank = 1;
    let choiceEntryIndex = 0;
    let isPurchased = 0;
    let isRanked = 0;
    let isChoiceNode = 0;

    if (selected) {
      if (!node) {
        unknownNodes.push(nodeId);
      }

      rank = node?.maxRanks || 1;
      choiceEntryIndex = 0;

      isPurchased = getBits(1, `is_purchased for ${node?.name} (${nodeId})`);
      if (isPurchased) {
        isRanked = getBits(1, `is_ranked for ${node?.name} (${nodeId})`);
        if (isRanked) {
          rank = getBits(RANK_BITS, `rank for ${node?.name} (${nodeId})`);
        }

        isChoiceNode = getBits(1, `is_choice for ${node?.name} (${nodeId})`);
        if (isChoiceNode) {
          choiceEntryIndex = getBits(CHOICE_BITS, `choice for ${node?.name} (${nodeId})`);
        }

        const entry = node?.entries[choiceEntryIndex];

        if (entry) {
          entries.push({
            id: entry.id,
            rank,
          });
        } else {
          unknownNodes.push(nodeId);
        }
      }
    }

    records.push({
      nodeId,
      selected,
      name: node?.name,
      isPurchased,
      isRanked,
      rank,
      isChoiceNode,
      choiceEntryIndex,
    });
  }

  if (DEBUG_TRAIT_PARSING) {
    /* eslint-disable no-console */
    console.group('parse table');
    //  console.table(records);
    //console.table(_.filter(records, { nodeId: 90261 }));
    console.table(records.slice(TABLE_REPORT_START, TABLE_REPORT_START + TABLE_REPORT_COUNT));
    console.groupEnd('parse table');
    /* eslint-enable no-console */
  }

  const bytesRead = _.ceil(head / BYTE_SIZE) * BYTE_SIZE;
  const bytesTotal = str.length * BYTE_SIZE;
  if (DEBUG_TRAIT_PARSING && bytesRead !== bytesTotal) {
    // eslint-disable-next-line no-console
    console.warn(`Error parsing talent string ${str}`);
    // eslint-disable-next-line no-console
    console.warn(`Read ${bytesRead} of ${bytesTotal} bytes! Possible parsing error!`);
  }

  if (DEBUG_TRAIT_PARSING && unknownNodes.length) {
    // eslint-disable-next-line no-console
    console.warn(`Unknkown nodes for talent string ${str}`);
    // eslint-disable-next-line no-console
    console.warn(`${_.join(unknownNodes, ', ')}`);
  }

  const ret = {
    version,
    specId,
    treeHash,
    entries,
    unknownNodes,
  };

  return ret;
};

const parse = (str, incSpecId = null) => {
  let specId = incSpecId;

  if (!str && !specId) {
    // TODO: Throw an error and clean up all the resulting mess
    return null;
  }

  // already parsed
  if (str?.specId && str?.entries) {
    return str;
  }

  // pre-dragonflight talent string, not handled so just return null
  if (_.isString(str) && str.length === 7) {
    return null;
  }

  // older URL talent format that SimC somewhat supports
  if (_.startsWith(str, 'http:') || _.startsWith(str, 'https:')) {
    return null;
  }

  const spent = {
    spec: 0,
    class: 0,
    subtree: 0,
    hero: 0,
  };

  let treeData;
  const treeFields = ['treeId', 'className', 'classId', 'specName', 'specId', 'fullNodeOrder'];

  let entriesStr = str;
  let unknownEntries = [];

  // if it looks like a base64 string from in-game, convert the base64 into a talents string
  if (looksLikeBlizzard(str)) {
    const talentInfo = parseBlizzardTalents(str);
    if (!talentInfo) {
      return null;
    }
    entriesStr = _.join(
      _.map(talentInfo.entries, entry => _.join([entry.id, entry.rank], ':')),
      '/',
    );
    treeData = getTreeBySpecId(talentInfo.specId);
    unknownEntries = talentInfo.unknownNodes;
    specId = talentInfo.specId;
  }

  const rawEntries = splitTalents(entriesStr);
  const entries = _.filter(_.map(rawEntries, ({ entryId, rank }) => {
    // find entry
    const entry = getTraitEntry(specId, entryId);
    if (!entry) {
      // check to see if the entry exists for the class but maybe in a hero tree that this
      // spec doesn't have access to. This is weird but seems can happen in game.
      if (allTraitEntryLookup[entryId]?.classId !== treeData?.classId) {
        unknownEntries.push(entryId);
      }
      return null;
    }
    if (entry.node.freeNode) {
      // skip free nodes since these are not an explicit choice
      // this simplifies some other stuff in the code (counting spent points without needing to know all node info)
      // I sort of have a bad feeling about this
      return null;
    }
    if (!treeData && entry.tree === 'spec') {
      treeData = _.pick(getTreeBySpecId(entry.specId), treeFields);
    }
    spent[entry.tree] += rank;
    return {
      entry: _.omit(entry, treeFields),
      rank,
    };
  }));
  if (!treeData && specId) {
    const tree = getTreeBySpecId(specId);
    treeData = _.pick(tree, treeFields);
  }

  // determine which hero tree is active
  const activeSubtrees = _.map(_.filter(entries, e => e.entry.type === 'subtree'), 'entry.traitSubTreeId');

  // re-calculate spent for hero tree since we know the active sub tree now
  spent.hero = _.sumBy(_.filter(entries, { entry: { node: { subTreeId: _.first(activeSubtrees) } } }), 'rank');

  const result = {
    ...treeData,
    spent,
    entries: _.sortBy(entries, ['entry.node.posY', 'entry.node.posX']),
    activeSubtrees,
    rawString: entriesStr,
    rawInput: str,
    unknownEntries,
  };

  return result;
};

/*
const stringifyBlizzardV1 = talents => {
  if (!talents) {
    return '';
  }
  if (!talents.specId) {
    return '';
  }

  let str = '';
  let head = 0;
  let byte = 0;

  const putBits = (bits, value) => {
    for (let i = 0; i < bits; i += 1) {
      const bit = head % BYTE_SIZE;
      // eslint-disable-next-line no-bitwise
      byte += ((value >> i) & 0b1) << bit;
      if (bit === BYTE_SIZE - 1) {
        str += base64Char[byte];
        byte = 0;
      }
      head += 1;
    }
  };
  const finish = () => {
    if (head % BYTE_SIZE) {
      str += base64Char[byte];
    }
  };

  const tree = getTreeBySpecId(talents.specId);

  const entryLookup = _.keyBy(talents.entries, 'entry.nodeId');

  putBits(VERSION_BITS, 1);
  putBits(SPEC_BITS, talents.specId);
  putBits(TREE_BITS, 0); // 0-filled to bypass in-game validation

  for (let i = 0; i < tree.fullNodeOrder.length; i += 1) {
    const node = getTraitNode(tree.fullNodeOrder[i], talents.specId);
    const entry = entryLookup[node?.id];
    const entryInfo = getTraitEntry(talents.specId, entry?.entry?.id);
    const selected = Boolean(entry);
    putBits(1, selected ? 1 : 0);
    if (entry) {
      const isPartialRanked = entry.rank !== entryInfo?.maxRanks;
      putBits(1, isPartialRanked ? 1 : 0);
      if (isPartialRanked) {
        putBits(RANK_BITS, entry.rank);
      }

      const isChoiceNode = node.type === 'choice';
      putBits(1, isChoiceNode ? 1 : 0);
      if (isChoiceNode) {
        const index = _.findIndex(node.entries, { id: entry.entry.id });
        if (index < 0) {
          throw new Error('Unable to find choice entry which is quite unexpected');
        }
        putBits(CHOICE_BITS, index);
      }
    }
  }

  finish();

  return str;
};
*/

const stringifyBlizzardV2 = talents => {
  if (!talents) {
    return '';
  }
  if (!talents.specId) {
    return '';
  }

  let str = '';
  let head = 0;
  let byte = 0;

  const putBits = (bits, value) => {
    for (let i = 0; i < bits; i += 1) {
      const bit = head % BYTE_SIZE;
      // eslint-disable-next-line no-bitwise
      byte += ((value >> i) & 0b1) << bit;
      if (bit === BYTE_SIZE - 1) {
        str += base64Char[byte];
        byte = 0;
      }
      head += 1;
    }
  };
  const finish = () => {
    if (head % BYTE_SIZE) {
      str += base64Char[byte];
    }
  };

  const tree = getTreeBySpecId(talents.specId);

  const entryLookup = _.keyBy(talents.entries, 'entry.nodeId');

  putBits(VERSION_BITS, 2);
  putBits(SPEC_BITS, talents.specId);
  putBits(TREE_BITS, 0); // 0-filled to bypass in-game validation

  const records = [];

  for (let i = 0; i < tree.fullNodeOrder.length; i += 1) {
    const nodeId = tree.fullNodeOrder[i];
    const node = getTraitNode(nodeId, talents.specId);

    const isPurchaseable = node && node.availableToSpec && !node.freeNode;
    const entry = entryLookup[node?.id];
    const entryInfo = getTraitEntry(talents.specId, entry?.entry?.id);
    const selected = Boolean(entry) || (node?.availableToSpec && node?.freeNode);

    let isPurchased = false;
    let isRanked = false;
    let rank = 1;
    let isChoiceNode = false;
    let choiceEntryIndex = 0;

    putBits(1, selected ? 1 : 0);
    if (selected) {
      isPurchased = isPurchaseable;
      putBits(1, isPurchased ? 1 : 0);
      if (isPurchased && entry) {
        isRanked = entryInfo?.maxRanks && entry.rank !== entryInfo?.maxRanks;
        putBits(1, isRanked ? 1 : 0);
        if (isRanked) {
          rank = entry.rank;
          putBits(RANK_BITS, rank);
        }

        isChoiceNode = node.type === 'choice' || node.type === 'subtree';
        putBits(1, isChoiceNode ? 1 : 0);
        if (isChoiceNode) {
          choiceEntryIndex = _.findIndex(node.entries, { id: entry.entry.id });
          if (choiceEntryIndex < 0) {
            /* eslint-disable no-console */
            console.error('Unable to find choice entry which is quite unexpected');
            console.error('Do not return an export, this is bad data');
            /* eslint-enable no-console */
          }
          putBits(CHOICE_BITS, choiceEntryIndex);
        }
      }
    }

    records.push({
      nodeId,
      selected: selected ? 1 : 0,
      name: node?.name,
      isPurchased: isPurchased ? 1 : 0,
      isRanked: isRanked ? 1 : 0,
      rank,
      isChoiceNode: isChoiceNode ? 1 : 0,
      choiceEntryIndex,
    });
  }

  finish();

  if (DEBUG_TRAIT_PARSING) {
    /* eslint-disable no-console */
    console.group('stringify table');
    //  console.table(records);
    //console.table(_.filter(records, { nodeId: 90261 }));
    console.table(records.slice(TABLE_REPORT_START, TABLE_REPORT_START + TABLE_REPORT_COUNT));
    console.groupEnd('stringify table');
    /* eslint-enable no-console */
  }

  return str;
};

const stringifySimcClassTalents = talents => {
  return _.map(talents.entries, t => _.join([t.entry.id, t.rank], ':')).join('/');
};

const stringify = (talents, format = 'blizzard') => {
  if (_.isEmpty(talents)) {
    return null;
  }
  switch (format) {
    case 'blizzardV1':
    case 'blizzard':
      return stringifyBlizzardV2(talents);
    case 'simc_trait_entry':
      return stringifySimcClassTalents(talents);
    default:
      throw new Error(`TalentTraits.stringify: Unknown format ${format}`);
  }
};

const normalize = str => stringify(parse(str));

const getPointsForLevel = level => {
  const base = level - 10;
  return {
    class: _.min([_.floor(base / 2) + 1, 31]),
    spec: _.min([_.ceil(base / 2), 30]),
    subtree: 1,
    hero: (level > 70 ? _.min([10, level - 70]) : 0),
  };
};

const diff = (a, b) => {
  let aParsed = a;
  let bParsed = b;

  if (_.isString(a)) {
    aParsed = parse(a);
  }
  if (_.isString(b)) {
    bParsed = parse(b);
  }

  if (!aParsed || !bParsed) {
    return null;
  }

  const collect = {};
  const entries = {};
  const iMap = ['a', 'b'];
  _.each([aParsed, bParsed], (talents, i) => {
    _.each(talents.entries, entry => {
      const key = _.join([entry.entry.id, entry.rank], '-');
      if (!collect[key]) {
        collect[key] = [];
      }
      collect[key].push(iMap[i]);
      entries[key] = entry;
    });
  });

  const shared = [];
  const onlyA = [];
  const onlyB = [];

  _.each(collect, (inWhich, key) => {
    const entry = entries[key];
    let which;
    if (collect[key].length === 2) {
      which = shared;
    } else if (_.first(collect[key]) === 'a') {
      which = onlyA;
    } else {
      which = onlyB;
    }
    which.push(entry);
  });

  return {
    shared,
    onlyA,
    onlyB,
    a: aParsed,
    b: bParsed,
  };
};

export default {
  BASE_COLUMN_WIDTH,

  parse,
  stringify,
  normalize,

  diff,

  getTreeBySpecId,
  getTreeByTreeId,
  getSubTreeById,

  getTraitNode,

  getDisplayAdjustedNodes,
  getBounds,

  getPointsForLevel,
};
